/**
 * Combo image swapping for case + strap bundle PDPs.
 *
 * Reads #combo-variant-map-{section.id}, listens for Wristbuddy variant
 * changes and strap radio changes, and updates gallery slots plus cart
 * line-item properties.
 */
(function () {
  const SELECTORS = {
    product: 'product-component',
    map: 'script[id^="combo-variant-map-"]',
    form: 'form[data-product-form]',
    variantInput: 'form[data-product-form] input[name="id"]',
    strapInput: '[data-combo-strap-input]',
    strapOption: '[data-strap-option]',
    strapLabel: '[data-strap-selected-label]',
    comboSlot: '[data-combo-slot]',
    comboSlide: '[data-combo-slide]',
    strapSlot: '[data-strap-slot]',
    caseSlot: '[data-case-slot]',
    comboThumb: '[data-combo-slot-thumb]',
    comboThumbSlide: '[data-combo-thumb-slide]',
    strapThumb: '[data-strap-slot-thumb]',
    caseThumb: '[data-case-slot-thumb]',
    comboProperty: '[data-combo-image-property]',
    strapProperty: '[data-combo-strap-property]',
    strapDisplayProperty: '[data-combo-strap-display-property]',
    bundleTitleProperty: '[data-combo-bundle-title-property]',
    caseTitleProperty: '[data-combo-case-title-property]',
    caseImageProperty: '[data-combo-case-image-property]',
    strapDisplayTitleProperty: '[data-combo-strap-display-title-property]',
    strapImageProperty: '[data-combo-strap-image-property]',
  };

  class ComboImageSwap {
    constructor(root) {
      this.root = root;
      this.mapEl = document.querySelector(SELECTORS.map);
      this.map = this.parseMap();
      this.currentCase = this.map?.currentCase || null;
      this.currentVariantMeta = null;
      this.urlStrapPreference = this.getUrlStrapPreference();
      this.shouldSyncStrapToUrl = Boolean(
        this.urlStrapPreference.handle || this.urlStrapPreference.variantId
      );
      this.abortController = new AbortController();

      this.onStrapChange = this.onStrapChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onVariantInputChange = this.onVariantInputChange.bind(this);
    }

    init() {
      if (!this.map || !this.root) return;

      const signal = this.abortController.signal;
      document.addEventListener('change', this.onStrapChange, { signal });
      document.addEventListener('variant:change', this.onVariantChange, { signal });
      document.addEventListener('variant:changed', this.onVariantChange, { signal });

      document.querySelectorAll(SELECTORS.variantInput).forEach((input) => {
        input.addEventListener('change', this.onVariantInputChange, { signal });
      });

      this.syncFromCurrentVariant();
      this.applyCurrentSelection();
    }

    destroy() {
      this.abortController.abort();
    }

    parseMap() {
      if (!this.mapEl) return null;

      try {
        return JSON.parse(this.mapEl.textContent);
      } catch (error) {
        console.error('Combo variant map could not be parsed', error);
        return null;
      }
    }

    onStrapChange(event) {
      if (!event.target.matches(SELECTORS.strapInput)) return;
      this.shouldSyncStrapToUrl = true;
      this.urlStrapPreference = {
        handle: event.target.dataset.strapHandle || '',
        variantId: event.target.value || '',
      };
      this.applyCurrentSelection({ syncUrl: true });
    }

    onVariantChange(event) {
      const mediaId = event.detail?.new_image ? String(event.detail.new_image) : null;

      if (mediaId) {
        const caseHandle = this.findCaseHandleByMediaId(mediaId);
        if (caseHandle) {
          this.currentCase = caseHandle;
        }
      }

      window.requestAnimationFrame(() => {
        this.syncFromCurrentVariant();
        this.applyCurrentSelection();
      });
    }

    onVariantInputChange() {
      this.syncFromCurrentVariant();
      this.applyCurrentSelection();
    }

    syncFromCurrentVariant() {
      const variantId = this.getCurrentVariantId();
      this.currentVariantMeta = this.getVariantMeta(variantId);
      const caseHandle = this.currentVariantMeta?.caseHandle || this.findCaseHandleByVariantId(variantId);

      if (caseHandle) {
        this.currentCase = caseHandle;
      }
    }

    getCurrentVariantId() {
      const input = document.querySelector(SELECTORS.variantInput);
      return input?.value ? String(input.value) : null;
    }

    getCurrentCaseData() {
      if (!this.map?.cases) return null;
      return this.map.cases[this.currentCase] || null;
    }

    findCaseHandleByVariantId(variantId) {
      if (!variantId || !this.map?.cases) return null;

      const variantMeta = this.getVariantMeta(variantId);
      if (variantMeta?.caseHandle) return variantMeta.caseHandle;

      return Object.keys(this.map.cases).find((caseHandle) => {
        return String(this.map.cases[caseHandle]?.variantId) === String(variantId);
      });
    }

    getVariantMeta(variantId) {
      if (!variantId || !this.map?.variants) return null;
      return this.map.variants[String(variantId)] || this.map.variants[variantId] || null;
    }

    findCaseHandleByMediaId(mediaId) {
      if (!mediaId || !this.map?.cases) return null;

      return Object.keys(this.map.cases).find((caseHandle) => {
        return String(this.map.cases[caseHandle]?.featuredMediaId) === String(mediaId);
      });
    }

    getAllowedStrapHandles(caseData) {
      if (!caseData) return [];

      const order = Array.isArray(caseData.strapOrder) && caseData.strapOrder.length
        ? caseData.strapOrder
        : this.map.defaultStrapOrder;

      const fallback = Object.keys(caseData.straps || {});
      const handles = Array.isArray(order) && order.length ? order : fallback;

      return handles.filter((handle) => {
        return caseData.straps?.[handle] && this.getComboImages(caseData, handle).length;
      });
    }

    getComboImages(caseData, strapHandle) {
      const combos = caseData?.combos?.[strapHandle];
      const list = Array.isArray(combos) ? combos : (combos?.url ? [combos] : []);
      return list.filter((image) => image?.url).sort((a, b) => Number(a.mediaId) - Number(b.mediaId));
    }

    syncStrapOptions(caseData) {
      const handles = this.getAllowedStrapHandles(caseData);
      const optionRoot = document.querySelector('[data-strap-selector] .pm__strap-selector__options');

      if (!optionRoot) {
        if (!handles.length) return null;

        const preferredInput = this.getPreferredStrapInput(handles);
        if (preferredInput) return preferredInput;

        const checkedInput = document.querySelector(`${SELECTORS.strapInput}:checked`);
        if (checkedInput && handles.includes(checkedInput.dataset.strapHandle)) {
          return checkedInput;
        }

        return handles
          .map((handle) => this.findStrapInput(handle, false) || this.findStrapInput(handle, true))
          .find(Boolean) || null;
      }

      const visibleHandles = new Set(handles);

      document.querySelectorAll(SELECTORS.strapOption).forEach((option) => {
        const handle = option.getAttribute('data-strap-handle');
        const isVisible = visibleHandles.has(handle);
        option.hidden = !isVisible;
        option.classList.toggle('is-hidden', !isVisible);
        option.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });

      if (!handles.length) {
        this.refreshStrapSwiper(optionRoot);
        return null;
      }

      handles.forEach((handle) => {
        const option = this.findStrapOption(handle);
        if (option) optionRoot.appendChild(option);
      });

      this.refreshStrapSwiper(optionRoot);

      const preferredInput = this.getPreferredStrapInput(handles);
      if (preferredInput) {
        preferredInput.checked = true;
        return preferredInput;
      }

      const checkedInput = document.querySelector(`${SELECTORS.strapInput}:checked`);
      if (checkedInput && handles.includes(checkedInput.dataset.strapHandle)) {
        return checkedInput;
      }

      const nextInput = handles
        .map((handle) => this.findStrapInput(handle, false))
        .find(Boolean)
        || handles
          .map((handle) => this.findStrapInput(handle, true))
          .find(Boolean);

      if (nextInput) {
        nextInput.checked = true;
      }

      return nextInput || null;
    }

    refreshStrapSwiper(optionRoot) {
      const swiperEl = optionRoot?.closest('swiper-init');
      const swiper = swiperEl?.swiper?.swiperElement;
      if (swiper && typeof swiper.update === 'function') {
        swiper.update();
      }
    }

    slideToStrapInput(input) {
      if (!input) return;

      const slide = input.closest('.swiper-slide');
      const swiperEl = input.closest('swiper-init');
      if (!slide || !swiperEl?.swiper) return;

      const slides = [...slide.parentElement.querySelectorAll(':scope > .swiper-slide:not([hidden]):not(.is-hidden)')];
      const index = slides.indexOf(slide);
      if (index >= 0) {
        swiperEl.swiper.selectCell(index);
      }
    }

    findStrapOption(handle) {
      return Array.from(document.querySelectorAll(SELECTORS.strapOption)).find((option) => {
        return option.getAttribute('data-strap-handle') === handle;
      }) || null;
    }

    findStrapInput(handle, includeDisabled) {
      return Array.from(document.querySelectorAll(SELECTORS.strapInput)).find((input) => {
        if (input.dataset.strapHandle !== handle) return false;
        return includeDisabled || !input.disabled;
      }) || null;
    }

    findStrapInputByVariantId(variantId, includeDisabled) {
      if (!variantId) return null;

      return Array.from(document.querySelectorAll(SELECTORS.strapInput)).find((input) => {
        if (String(input.value) !== String(variantId)) return false;
        return includeDisabled || !input.disabled;
      }) || null;
    }

    getPreferredStrapInput(handles) {
      const preferred = this.urlStrapPreference || {};
      const byVariant = preferred.variantId
        ? this.findStrapInputByVariantId(preferred.variantId, false)
        : null;
      if (byVariant && handles.includes(byVariant.dataset.strapHandle)) {
        return byVariant;
      }

      const byHandle = preferred.handle
        ? this.findStrapInput(preferred.handle, false)
        : null;
      if (byHandle && handles.includes(byHandle.dataset.strapHandle)) {
        return byHandle;
      }

      return null;
    }

    applyCurrentSelection(options = {}) {
      const caseData = this.getCurrentCaseData();
      if (!caseData) return;

      const strapInput = this.syncStrapOptions(caseData);
      const strapHandle = strapInput?.dataset.strapHandle || null;
      const strapData = strapHandle ? caseData.straps?.[strapHandle] : null;
      const comboImages = strapHandle ? this.getComboImages(caseData, strapHandle) : [];
      const comboSlidesData = comboImages.length ? comboImages : (caseData.case?.url ? [caseData.case] : []);
      const comboData = comboImages[0] || null;

      this.syncComboBlocks(comboSlidesData);
      this.updateSlot(SELECTORS.caseSlot, caseData.case);

      if (strapData?.image) {
        this.updateSlot(SELECTORS.strapSlot, strapData.image);
      }

      this.updateThumb(SELECTORS.caseThumb, caseData.case);

      if (strapData?.image) {
        this.updateThumb(SELECTORS.strapThumb, strapData.image);
      }

      this.updateSelectedLabel(strapData);
      this.updateFormProperties(caseData, strapData, comboData);
      this.slideToStrapInput(strapInput);

      if (options.syncUrl || this.shouldSyncStrapToUrl) {
        this.updateUrlStrapParam(strapInput);
      }
    }

    sanitizeSwiperSlide(slide) {
      if (!slide) return;

      slide.classList.remove(
        'swiper-slide-thumb-active',
        'swiper-slide-active',
        'swiper-slide-next',
        'swiper-slide-prev',
        'swiper-slide-visible',
        'swiper-slide-fully-visible'
      );
      slide.removeAttribute('aria-label');
      slide.removeAttribute('aria-current');
      slide.style.removeProperty('width');
      slide.style.removeProperty('margin-right');
    }

    refreshSwiperInstance(swiperHost, options = {}) {
      const instance = swiperHost?.swiper?.swiperElement;
      if (!instance) return;

      try {
        instance.updateSlides?.();
        instance.update?.();

        if (!options.isThumb && instance.thumbs?.swiper) {
          instance.thumbs.swiper.updateSlides?.();
          instance.thumbs.swiper.update?.();

          if (typeof instance.thumbs.update === 'function') {
            instance.thumbs.update(true);
          }
        }

        if (typeof options.slideTo === 'number' && typeof instance.slideTo === 'function') {
          instance.slideTo(options.slideTo, 0);
        }
      } catch (_) {
        // Gallery should keep working even if Swiper refresh fails.
      }
    }

    createComboSlideTemplate(wrapper, anchorSelector, isThumb) {
      const anchor = wrapper.querySelector(anchorSelector);
      if (!anchor) return null;

      const template = anchor.cloneNode(true);
      this.sanitizeSwiperSlide(template);

      template.removeAttribute('data-strap-slot');
      template.removeAttribute('data-strap-slot-thumb');
      template.removeAttribute('data-case-slot');
      template.removeAttribute('data-case-slot-thumb');
      template.removeAttribute('data-combo-slot');
      template.removeAttribute('data-combo-slot-thumb');
      template.setAttribute(isThumb ? 'data-combo-thumb-slide' : 'data-combo-slide', '');

      template.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      template.querySelectorAll('[data-combo-image], [data-combo-thumb]').forEach((el) => {
        el.removeAttribute('data-combo-image');
        el.removeAttribute('data-combo-thumb');
      });

      return template;
    }

    syncComboBlocks(images) {
      const configs = [
        { anchor: SELECTORS.strapSlot, item: SELECTORS.comboSlide, swiper: '#productSwiperElement', isThumb: false },
        { anchor: SELECTORS.strapThumb, item: SELECTORS.comboThumbSlide, swiper: '[id^="productSwiperThumbs--"]', isThumb: true },
      ];
      const swiperHosts = [];

      configs.forEach(({ anchor, item, swiper, isThumb }) => {
        const wrapper = document.querySelector(anchor)?.closest('.swiper-wrapper');
        if (!wrapper) return;

        let nodes = [...wrapper.querySelectorAll(item)];
        if (!images.length) {
          nodes.forEach((node) => node.remove());
        } else {
          if (!nodes.length) {
            const template = this.createComboSlideTemplate(wrapper, anchor, isThumb);
            if (template) {
              const anchorEl = wrapper.querySelector(anchor);
              wrapper.insertBefore(template, anchorEl);
              nodes = [template];
            }
          }

          if (nodes.length) {
            const template = nodes[0];
            while (nodes.length < images.length) {
              const clone = template.cloneNode(true);
              this.sanitizeSwiperSlide(clone);
              wrapper.insertBefore(clone, nodes.at(-1).nextSibling);
              nodes = [...wrapper.querySelectorAll(item)];
            }
            while (nodes.length > images.length) nodes.pop().remove();
            [...wrapper.querySelectorAll(item)].forEach((node, index) => {
              this.applyComboImage(node, images[index], index === 0, isThumb);
            });
          }
        }

        if (isThumb) {
          [...wrapper.querySelectorAll(':scope > .swiper-slide')].forEach((slide, index) => {
            this.sanitizeSwiperSlide(slide);
            slide.dataset.index = String(index);
          });
        }

        const host = document.querySelector(swiper);
        if (host) swiperHosts.push({ host, isThumb });
      });

      // Thumbs first — main swiper thumbs module depends on the updated thumb count.
      swiperHosts
        .sort((a, b) => Number(b.isThumb) - Number(a.isThumb))
        .forEach(({ host, isThumb }) => {
          this.refreshSwiperInstance(host, { slideTo: 0, isThumb });
        });
    }

    applyComboImage(node, imageData, isPrimary, isThumb) {
      if (!imageData) return;

      if (isThumb) {
        if (!imageData.thumb && !imageData.url) return;
        const image = node.querySelector('.ProductThumb--Image');
        if (!image) return;
        node.toggleAttribute('data-combo-slot-thumb', isPrimary);
        image.toggleAttribute('data-combo-thumb', isPrimary);
        image.src = imageData.thumb || this.withWidth(imageData.url, 130);
        image.removeAttribute('srcset');
        image.alt = imageData.alt || '';
        return;
      }

      if (!imageData.url) return;
      const image = node.querySelector('.ProductSlider--Image');
      const mediaEl = node.querySelector('.ProductSlider--Element');
      if (!image) return;

      const width = imageData.width || 1200;
      const height = imageData.height || 1200;
      node.toggleAttribute('data-combo-slot', isPrimary);
      image.toggleAttribute('data-combo-image', isPrimary);
      image.classList.toggle('ProductSlider--ImageFirst', isPrimary);
      image.src = this.withWidth(imageData.url, 800);
      image.srcset = [
        `${this.withWidth(imageData.url, 400)} 400w`,
        `${this.withWidth(imageData.url, 800)} 800w`,
        `${this.withWidth(imageData.url, 1200)} 1200w`,
      ].join(', ');
      image.sizes = '(min-width: 1025px) 50vw, 100vw';
      image.width = width;
      image.height = height;
      image.dataset.maxWidth = String(width);
      image.dataset.maxHeight = String(height);
      image.dataset.originalSrc = imageData.zoomUrl || this.withWidth(imageData.url, 3500);
      image.alt = imageData.alt || '';
      if (mediaEl) mediaEl.style.setProperty('--aspect-ratio', String(width / height || 1));
    }

    updateSlot(selector, imageData) {
      if (!imageData?.url) return;

      const slot = document.querySelector(selector);
      const image = slot?.querySelector('.ProductSlider--Image');
      const mediaEl = slot?.querySelector('.ProductSlider--Element');
      if (!slot || !image) return;

      const width = imageData.width || 1200;
      const height = imageData.height || 1200;
      const aspectRatio = width && height ? width / height : 1;

      image.src = this.withWidth(imageData.url, 800);
      image.srcset = [
        `${this.withWidth(imageData.url, 400)} 400w`,
        `${this.withWidth(imageData.url, 800)} 800w`,
        `${this.withWidth(imageData.url, 1200)} 1200w`,
      ].join(', ');
      image.sizes = '(min-width: 1025px) 50vw, 100vw';
      image.width = width;
      image.height = height;
      image.dataset.maxWidth = String(width);
      image.dataset.maxHeight = String(height);
      image.dataset.originalSrc = imageData.zoomUrl || this.withWidth(imageData.url, 3500);
      image.alt = imageData.alt || '';

      if (mediaEl) {
        mediaEl.style.setProperty('--aspect-ratio', String(aspectRatio));
      }
    }

    updateThumb(selector, imageData) {
      if (!imageData?.thumb && !imageData?.url) return;

      const thumb = document.querySelector(selector);
      const image = thumb?.querySelector('.ProductThumb--Image');
      if (!image) return;

      image.src = imageData.thumb || this.withWidth(imageData.url, 130);
      image.removeAttribute('srcset');
      image.alt = imageData.alt || '';
    }

    updateSelectedLabel(strapData) {
      const label = document.querySelector(SELECTORS.strapLabel);
      if (!label) return;

      label.textContent = strapData?.productTitle || '';
    }

    updateFormProperties(caseData, strapData, comboData) {
      const strapVariantId = strapData?.variantId || '';
      const strapTitle = strapData?.productTitle || '';
      const comboImage = comboData?.url ? this.toAbsoluteUrl(comboData.url) : '';
      const variantTitle = this.currentVariantMeta?.variantTitle || caseData.variantTitle;
      const caseImage = caseData.case?.url ? this.toAbsoluteUrl(caseData.case.url) : '';
      const strapImage = strapData?.image?.url ? this.toAbsoluteUrl(strapData.image.url) : '';
      const bundleTitle = strapTitle
        ? `${variantTitle} + ${strapTitle}`
        : variantTitle || '';

      document.querySelectorAll(SELECTORS.form).forEach((form) => {
        this.setInputValue(form, SELECTORS.strapProperty, strapVariantId);
        this.setInputValue(form, SELECTORS.strapDisplayProperty, this.cleanDisplayTitle(strapTitle));
        this.setInputValue(form, SELECTORS.comboProperty, comboImage);
        this.setInputValue(form, SELECTORS.bundleTitleProperty, bundleTitle);
        this.setInputValue(form, SELECTORS.caseTitleProperty, this.cleanDisplayTitle(variantTitle));
        this.setInputValue(form, SELECTORS.caseImageProperty, caseImage);
        this.setInputValue(form, SELECTORS.strapDisplayTitleProperty, this.cleanDisplayTitle(strapTitle));
        this.setInputValue(form, SELECTORS.strapImageProperty, strapImage);
      });
    }

    setInputValue(form, selector, value) {
      const input = form.querySelector(selector);
      if (!input) return;

      input.value = value || '';
    }

    getUrlStrapPreference() {
      try {
        const params = new URL(window.location.href).searchParams;
        return {
          handle: params.get('strap') || params.get('strap_handle') || '',
          variantId: params.get('strap_variant') || params.get('strap_variant_id') || '',
        };
      } catch (error) {
        return { handle: '', variantId: '' };
      }
    }

    updateUrlStrapParam(strapInput) {
      if (!strapInput || !window.history?.replaceState) return;

      try {
        const url = new URL(window.location.href);
        const handle = strapInput.dataset.strapHandle || '';
        const variantId = strapInput.value || '';

        if (handle) {
          url.searchParams.set('strap', handle);
        } else {
          url.searchParams.delete('strap');
        }

        if (variantId) {
          url.searchParams.set('strap_variant', variantId);
        } else {
          url.searchParams.delete('strap_variant');
        }

        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        this.urlStrapPreference = { handle, variantId };
        this.shouldSyncStrapToUrl = Boolean(handle || variantId);
      } catch (error) {
        // URL state is a convenience; gallery/cart state should still work without it.
      }
    }

    withWidth(url, width) {
      if (!url) return '';

      if (url.includes('width=')) {
        return url.replace(/width=\d+/g, `width=${width}`);
      }

      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}width=${width}`;
    }

    toAbsoluteUrl(url) {
      if (!url) return '';
      if (url.startsWith('https://')) return url;
      if (url.startsWith('//')) return `https:${url}`;
      if (url.startsWith('/')) return `${window.location.origin}${url}`;
      return url;
    }

    cleanDisplayTitle(title) {
      const value = String(title || '').trim();
      if (!value) return '';

      const parts = value.split(' - ').map((part) => part.trim()).filter(Boolean);
      return parts.length > 1 ? parts[parts.length - 1] : value;
    }
  }

  class ComboStrapSelector extends HTMLElement {}

  if (!customElements.get('combo-strap-selector')) {
    customElements.define('combo-strap-selector', ComboStrapSelector);
  }

  const boot = () => {
    const root = document.querySelector(SELECTORS.product);
    if (!root || !document.querySelector(SELECTORS.map)) return;

    window.comboImageSwap?.destroy?.();
    window.comboImageSwap = new ComboImageSwap(root);
    window.comboImageSwap.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
